/**
 * resolutionSignal
 *
 * A tiny shared Zustand store whose sole purpose is to broadcast the fact
 * that an attention-queue item was just resolved (approved / rejected /
 * suppressed) by the operator.
 *
 * Any hook that maintains its own independent polling loop — e.g.
 * ``useAwaitingApprovalRuns`` — can subscribe to ``count`` and trigger an
 * immediate refetch when it increments, giving the operator instant visual
 * feedback rather than waiting for the next polling tick.
 *
 * The counter is intentionally not persisted or reset — it only needs to
 * be monotonically increasing within a single browser session so that
 * ``useEffect([count])`` dependency arrays fire on every resolution.
 */

import { create } from 'zustand'

interface ResolutionSignalState {
  /** Incremented on every successful attention-item resolution. */
  count: number
  /** Called by the queue store immediately after a successful resolve. */
  increment: () => void
}

export const useResolutionSignal = create<ResolutionSignalState>((set) => ({
  count: 0,
  increment: () => set((s) => ({ count: s.count + 1 })),
}))
